import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, User, ArrowLeft, AlertCircle, Eye, EyeOff } from "lucide-react";
import { authAPI } from "../services/api";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authAPI.isAuthenticated()) {
      navigate("/admin");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await authAPI.login(username, password);
      const audio = new Audio("https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg");
      audio.play().catch(() => {});
      navigate("/admin");
    } catch (err) {
      setError(err.message || "Invalid credentials. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 relative overflow-hidden transition-colors duration-500">
      {/* Premium background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05)_0%,transparent_70%)] dark:bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.08)_0%,transparent_70%)] pointer-events-none" />

      {/* Floating Animated Glowing Orbs */}
      <motion.div
        animate={{
          y: [0, -30, 0],
          x: [0, 15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-10 left-[10%] w-72 h-72 bg-indigo-600/5 dark:bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none"
      />
      
      <motion.div
        animate={{
          y: [0, 40, 0],
          x: [0, -20, 0],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute bottom-10 right-[10%] w-96 h-96 bg-purple-600/5 dark:bg-purple-600/10 rounded-full blur-[120px] pointer-events-none"
      />

      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
        className="w-full max-w-md bg-white dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200 dark:border-gray-800 p-8 rounded-3xl shadow-xl dark:shadow-[0_0_50px_rgba(99,102,241,0.12)] z-10 relative text-gray-900 dark:text-gray-100"
      >
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center text-xs text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 mb-6 font-[Poppins] transition group"
        >
          <ArrowLeft size={14} className="mr-1 group-hover:-translate-x-1 transition-transform" /> Back to Site
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl text-indigo-500 dark:text-indigo-400 mb-4 shadow-inner">
            <Lock size={28} />
          </div>
          <h2 className="text-3xl font-extrabold tracking-wider font-[Cinzel] bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
            Admin Console
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 font-[Inter]">
            Sign in to unlock portfolio settings
          </p>
        </div>

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: "auto", y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              className="flex items-start gap-2.5 p-4 mb-6 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-500 dark:text-red-400 text-sm font-[Inter] overflow-hidden"
            >
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="space-y-6 font-[Poppins]">
          {/* Username */}
          <div className="space-y-2">
            <label className="text-xs text-gray-500 dark:text-gray-400 font-semibold tracking-wider block uppercase">
              Username
            </label>
            <div className="relative group">
              <span className="absolute inset-y-0 left-0 pl-4.5 flex items-center text-gray-400 dark:text-gray-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors">
                <User size={18} />
              </span>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Admin username"
                className="w-full pl-11 pr-4 py-3.5 bg-gray-50 dark:bg-gray-950/60 border border-gray-300 dark:border-gray-800/80 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 rounded-2xl text-gray-900 dark:text-white outline-none text-sm transition-all"
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="text-xs text-gray-500 dark:text-gray-400 font-semibold tracking-wider block uppercase">
              Password
            </label>
            <div className="relative group">
              <span className="absolute inset-y-0 left-0 pl-4.5 flex items-center text-gray-400 dark:text-gray-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors">
                <Lock size={18} />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-11 pr-12 py-3.5 bg-gray-50 dark:bg-gray-950/60 border border-gray-300 dark:border-gray-800/80 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 rounded-2xl text-gray-900 dark:text-white outline-none text-sm transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4.5 flex items-center text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <motion.button
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.985 }}
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-600 hover:from-indigo-600 hover:via-purple-700 hover:to-pink-700 text-white font-bold rounded-2xl shadow-[0_4px_20px_rgba(99,102,241,0.25)] dark:shadow-[0_4px_20px_rgba(99,102,241,0.3)] hover:shadow-[0_4px_25px_rgba(99,102,241,0.4)] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <span className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
            ) : (
              "Authorize Access"
            )}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}
